<script setup>
import { useForm } from '@inertiajs/vue3'
import AuthenticationCard from '@/Components/AuthenticationCard.vue'
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue'
import Checkbox from '@/Components/Checkbox.vue'
import InputError from '@/Components/InputError.vue'
import InputLabel from '@/Components/InputLabel.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import TextInput from '@/Components/TextInput.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineProps({
    canResetPassword: Boolean,
    status: String,
})

const form = useForm({
    email: '',
    password: '',
    remember: false,
})

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    })
}
</script>

<template>
    <AppLayout meta_title="Přihlásit se">
        <template #content>
            <AuthenticationCard>
                <div
                    v-if="status"
                    class="mb-4 text-sm font-medium text-green-600 dark:text-green-400"
                >
                    {{ status }}
                </div>

                <div class="sm:mx-auto sm:w-full sm:max-w-[480px]">
                    <div>
                        <form
                            class="space-y-6"
                            @submit.prevent="submit"
                        >
                            <div>
                                <InputLabel
                                    for="email"
                                    value="Email"
                                    isRequired
                                />
                                <TextInput
                                    id="email"
                                    v-model="form.email"
                                    type="email"
                                    class="mt-1 block w-full"
                                    autofocus
                                    autocomplete="username"
                                />
                                <InputError
                                    class="mt-2"
                                    :message="form.errors.email"
                                />
                            </div>

                            <div class="mt-4">
                                <InputLabel
                                    for="password"
                                    value="Password"
                                    isRequired
                                />
                                <TextInput
                                    id="password"
                                    v-model="form.password"
                                    type="password"
                                    class="mt-1 block w-full"
                                    autocomplete="current-password"
                                    required
                                />
                                <InputError
                                    class="mt-2"
                                    :message="form.errors.password"
                                />
                            </div>

                            <div class="mt-4 flex items-center justify-between">
                                <label class="flex items-center">
                                    <Checkbox
                                        v-model:checked="form.remember"
                                        name="remember"
                                    />
                                    <span class="ms-2 text-sm text-zinc-400"> Zapamatovat si mě </span>
                                </label>

                                <div class="text-sm/6">
                                    <Link
                                        v-if="canResetPassword"
                                        :href="route('password.request')"
                                        class="rounded-md text-sm text-zinc-400 underline hover:text-zinc-100"
                                    >
                                        Zapomněli jste heslo?
                                    </Link>
                                </div>
                            </div>

                            <div>
                                <PrimaryButton
                                    class="!dark:text-white w-full"
                                    :class="{ 'opacity-25': form.processing }"
                                    :disabled="form.processing"
                                >
                                    Přihlásit se
                                </PrimaryButton>
                            </div>
                        </form>

                        <div>
                            <div class="relative mt-10">
                                <div
                                    class="absolute inset-0 flex items-center"
                                    aria-hidden="true"
                                >
                                    <div class="w-full border-t border-gray-200 dark:border-zinc-900" />
                                </div>
                                <div class="relative flex justify-center text-sm/6 font-medium">
                                    <span class="rounded-xl bg-gray-200 px-6 text-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
                                        Nemáte účet ?
                                    </span>
                                </div>
                            </div>

                            <div class="mt-6 grid grid-cols-1 gap-4">
                                <Link
                                    :href="route('register')"
                                    class="flex w-full items-center justify-center gap-3 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-gray-300 ring-inset focus-visible:ring-transparent dark:bg-zinc-900 dark:text-zinc-900 dark:ring-zinc-700 dark:hover:bg-zinc-800"
                                >
                                    <span class="text-sm/6 font-semibold dark:text-white"> Zaregistrovat se </span>
                                </Link>

                                <a
                                    :href="route('google.redirect')"
                                    class="flex w-full items-center justify-center gap-3 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-gray-300 ring-inset focus-visible:ring-transparent dark:bg-zinc-900 dark:text-zinc-900 dark:ring-zinc-700 dark:hover:bg-zinc-800"
                                >
                                    <svg
                                        class="h-5 w-5"
                                        aria-hidden="true"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                                            fill="#EA4335"
                                        />
                                        <path
                                            d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                                            fill="#4285F4"
                                        />
                                        <path
                                            d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                                            fill="#FBBC05"
                                        />
                                        <path
                                            d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                                            fill="#34A853"
                                        />
                                    </svg>
                                    <span class="text-sm/6 font-semibold dark:text-white"> Google </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </AuthenticationCard>
        </template>
    </AppLayout>
</template>
